import React, { useState, useRef, useEffect } from 'react';
import Title from '../layouts/Title';
import ContactLeft from './ContactLeft';
import emailjs from '@emailjs/browser';
import Lottie from 'lottie-react';
import sendingAnimation from '../../assets/sending.json';
import successAnimation from '../../assets/success.json';

const Contact = () => {
  const form = useRef();
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTelegramAnimation, setShowTelegramAnimation] = useState(false);

  // Auto-hide success message after 10 seconds
  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => {
        setSuccessMsg('');
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleSend = (e) => {
    e.preventDefault();
    if (name.length === 0 || number.length === 0 || email.length === 0 || message.length === 0) {
      setErrMsg('All fields are required!');
    } else {
      setIsSubmitting(true);
      setShowTelegramAnimation(true);
      setErrMsg('');

      const serviceID = 'service_5hu2lsm';
      const templateID = 'template_sai07rm';
      const userID = '58ycV22bVcIdWwYBv';

      emailjs.send(serviceID, templateID, {
        name,
        number,
        email,
        message,
        from_name: name,
        from_number: number,
        from_email: email
      }, userID)
        .then((response) => {
          setIsSubmitting(false);
          setShowTelegramAnimation(false);
          setSuccessMsg(`Your request has been submitted successfully! We'll get back to you soon.`);
          setName('');
          setNumber('');
          setEmail('');
          setMessage('');
        }, (error) => {
          setIsSubmitting(false);
          setShowTelegramAnimation(false);
          console.error('There was an error!', error);
          setErrMsg('An error occurred. Please try again later.');
        });
    }
  };

  return (
    <section id="contact" className="w-full py-20 border-b-[1px] border-b-black">
      <div className="flex justify-center items-center text-center">
        <Title title="CONTACT" des="Contact With Me" />
      </div>
      <div className="w-full">
        <div className="w-full h-auto flex flex-col lgl:flex-row justify-between">
          <ContactLeft />
          <div className="w-full lgl:w-[60%] h-full py-10 bg-gradient-to-r from-[#1e2024] to-[#23272b] flex flex-col gap-8 p-4 lgl:p-8 rounded-lg shadow-shadowOne relative">
            
            {/* Telegram-style Sending Animation Overlay */}
            {showTelegramAnimation && (
              <div className="absolute inset-0 bg-black/90 backdrop-blur-lg rounded-lg flex flex-col items-center justify-center z-20 animate-fadeIn">
                <div className="text-center p-8">
                  {/* Telegram-like Paper Plane Animation */}
                  <div className="w-32 h-32 mx-auto mb-6 relative">
                    <div className="absolute inset-0 bg-teal-500 rounded-full animate-ping opacity-20"></div>
                    <div className="absolute inset-4 bg-gradient-to-br from-teal-400 to-blue-500 rounded-full flex items-center justify-center shadow-2xl">
                      <Lottie 
                        animationData={sendingAnimation} 
                        loop={true}
                        autoplay={true}
                        className="w-20 h-20"
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-teal-400 mb-2 animate-pulse">
                    Sending Your Message...
                  </h3>
                  <p className="text-gray-300">
                    Your message is flying through the internet
                  </p>
                  
                  {/* Loading Dots */}
                  <div className="flex justify-center mt-4 space-x-1">
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}

           {/* Success Overlay with Pure CSS Green Tick */}
{successMsg && (
  <div className="absolute inset-0 bg-gradient-to-br from-green-900/30 to-emerald-800/30 backdrop-blur-lg rounded-lg flex items-center justify-center z-20 animate-fadeIn">
    <div className="text-center p-8 max-w-md">
      {/* Pure CSS Green Tick */}
      <div className="w-32 h-32 mx-auto mb-6 relative">
        <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-20"></div>
        <div className="absolute inset-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-2xl">
          <div className="css-checkmark">
            <div className="css-checkmark-stem"></div>
            <div className="css-checkmark-kick"></div>
          </div>
        </div>
      </div>
      
      <h3 className="text-2xl font-bold text-green-400 mb-4">
        Message Sent!
      </h3>
      <p className="text-gray-200 text-lg mb-2">{successMsg}</p>
      <p className="text-green-300 text-sm animate-pulse">
        This message will disappear in 10 seconds...
      </p>
      
      <button 
        onClick={() => setSuccessMsg('')}
        className="mt-6 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg text-white font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg shadow-green-500/20"
      >
        Send Another Message
      </button>
    </div>
  </div>
)}

            <form ref={form} onSubmit={handleSend} className="w-full flex flex-col gap-4 lgl:gap-6 py-2 lgl:py-5">
              {errMsg && (
                <p className="py-3 bg-gradient-to-r from-[#1e2024] to-[#23272b] shadow-shadowOne text-center text-orange-500 text-base tracking-wide animate-bounce">
                  {errMsg}
                </p>
              )}
              
              <div className="w-full flex flex-col lgl:flex-row gap-10">
                <div className="w-full lgl:w-1/2 flex flex-col gap-4">
                  <p className="text-sm text-gray-400 uppercase tracking-wide">Your name</p>
                  <input
                    onChange={(e) => setName(e.target.value)}
                    value={name}
                    name='from_name'
                    className="contactInput"
                    type="text"
                    disabled={isSubmitting}
                  />
                </div>
                <div className="w-full lgl:w-1/2 flex flex-col gap-4">
                  <p className="text-sm text-gray-400 uppercase tracking-wide">Phone Number</p>
                  <input
                    onChange={(e) => setNumber(e.target.value)}
                    name='number'
                    value={number}
                    className="contactInput"
                    type="text"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">Email</p>
                <input
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  name='user_email'
                  className="contactInput"
                  type="email"
                  disabled={isSubmitting}
                />
              </div>
              
              <div className="flex flex-col gap-4">
                <p className="text-sm text-gray-400 uppercase tracking-wide">Message</p>
                <textarea
                  onChange={(e) => setMessage(e.target.value)}
                  value={message}
                  name='message'
                  className="contactTextArea"
                  cols="30"
                  rows="8"
                  disabled={isSubmitting}
                ></textarea>
              </div>
              
              <div className="w-full relative">
                <button
                  type="submit"
                  disabled={isSubmitting || successMsg}
                  className="w-full h-14 rounded-lg text-white font-bold tracking-wider uppercase transition-all duration-700 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 relative overflow-hidden group telegram-button"
                >
                  {/* Button Content */}
                  <div className="relative z-10 flex items-center justify-center">
                    {isSubmitting ? (
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 mr-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Sending...
                      </div>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-3 transition-all duration-300 group-hover:translate-x-1 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                        </svg>
                        Send Message
                      </>
                    )}
                  </div>

                  {/* Telegram-inspired Gradient */}
                  <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-blue-500 opacity-100 group-hover:from-teal-600 group-hover:to-blue-600 transition-all duration-500"></div>
                  
                  {/* Moving Shine */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out"></div>
                  
                  {/* Glow Effect */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg blur opacity-30 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }

        /* Telegram-inspired button styles */
        .telegram-button {
          background: linear-gradient(135deg, #0088cc 0%, #00aced 100%);
          box-shadow: 0 4px 15px 0 rgba(0, 136, 204, 0.3);
        }

        .telegram-button:hover {
          box-shadow: 0 8px 25px 0 rgba(0, 136, 204, 0.5);
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        /* Pure CSS Checkmark */
.css-checkmark {
  width: 40px;
  height: 40px;
  transform: rotate(45deg);
}

.css-checkmark-stem {
  position: absolute;
  width: 4px;
  height: 18px;
  background-color: white;
  left: 18px;
  top: 8px;
  animation: checkmarkStem 0.3s ease 0.8s both;
}

.css-checkmark-kick {
  position: absolute;
  width: 8px;
  height: 4px;
  background-color: white;
  left: 10px;
  top: 22px;
  animation: checkmarkKick 0.3s ease 1.1s both;
}

@keyframes checkmarkStem {
  0% { height: 0; }
  100% { height: 18px; }
}

@keyframes checkmarkKick {
  0% { width: 0; }
  100% { width: 8px; }
}
      `}</style>
    </section>
  );
};

export default Contact;